/* Every column the Contacts and Clients tables show, as pure functions.

   The rule this file exists to keep: a list column is derived from data the
   CRM already has in memory. Nothing here reads Firestore, and in particular
   nothing here reads the `interactions` collection — it holds email bodies,
   and a 3,400-row table must never pull them to print "7 mo ago". Last touch
   comes from the stored `lastTouch` field (CRM v3 slice 1); the drawer, which
   is one record at a time, still uses useInteractions.

   Money comes from src/apps/jobs/lib/revenue.js by way of src/lib/crmValue.js,
   so a change order counts on its parent project and never twice.

   No React, no DOM. mockups/crm-v3-contacts-v1.html sections #a and #clients
   are the visual spec; this is the arithmetic under them. */

import { CONSENT_STATES, INDUSTRY_LABELS, CONTACT_ROLES } from "../../lib/constants.js";
import { resolveQuoteRevenue } from "../../lib/builderHelpers.js";
import { contactValueIndex, jobAmount, jobDate, dateOnly } from "../../lib/crmValue.js";
import { isRolledUpChangeOrderJob } from "../jobs/lib/revenue.js";

const asArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);
const key = (v) => String(v ?? "").trim().toLowerCase();
const DAY = 86_400_000;

/* ═══ last touch ═══════════════════════════════════════════════════════════ */

/* The mono label beside the relative time. Channels are the seven
   tools/crm/lib.mjs writes; anything else prints nothing rather than a raw
   snake_case token. */
export const TOUCH_CHANNEL_LABELS = {
  mail_in: "MAIL IN",
  mail_out: "MAIL OUT",
  quote: "QUOTE",
  job: "JOB",
  campaign: "CAMPAIGN",
  note: "NOTE",
  session: "SESSION",
};

/**
 * The stored touch on a contact or a client.
 *
 * `lastTouch.at` holds an exact time when the writer had one and a date
 * otherwise, which is why a plain string compare orders touches correctly.
 * A record written before slice 1 has only `lastContact`, a date with no
 * channel — it still dates the row, it just cannot say how.
 */
export function lastTouchOf(record) {
  const at = String(record?.lastTouch?.at || "").trim();
  if (at) {
    const channel = String(record?.lastTouch?.channel || "").trim();
    return { at, channel, label: TOUCH_CHANNEL_LABELS[channel] || "" };
  }
  const fallback = dateOnly(record?.lastContact);
  return { at: fallback, channel: "", label: "" };
}

/** Whole days between a date and now, NEGATIVE when the date is ahead of now.
 *  Returns null when there is no parseable date at all. This is the honest
 *  arithmetic; daysSince() clamps it for display. */
export function signedDaysSince(at, now = new Date()) {
  const raw = String(at || "").trim();
  if (!raw) return null;
  const iso = raw.length <= 10 ? `${raw}T00:00:00Z` : raw;
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return null;
  const nowMs = now instanceof Date ? now.getTime() : Number(now);
  return Math.floor((nowMs - then) / DAY);
}

/** Whole days between a touch and now. Negative (a dated-forward record) reads
 *  as 0 rather than "-3 d" — a display clamp, and nothing that decides whether
 *  a date falls inside a window may use it. Null when there is no date. */
export function daysSince(at, now = new Date()) {
  const days = signedDaysSince(at, now);
  return days === null ? null : Math.max(0, days);
}

/**
 * "2 d", "3 wk", "7 mo", "2 y" — the mockup's ladder.
 *
 * Days up to a fortnight, then weeks while weeks still read as a count of
 * weeks (the mockup's "14 wk ago" for a rhythm broken in the spring), then
 * months, then years. Same day is "today". No date at all is "".
 */
export function relativeTime(at, now = new Date()) {
  const days = daysSince(at, now);
  if (days === null) return "";
  if (days === 0) return "today";
  if (days < 14) return `${days} d`;
  if (days < 112) return `${Math.round(days / 7)} wk`;
  if (days < 365) return `${Math.max(1, Math.round(days / 30.44))} mo`;
  return `${Math.floor(days / 365)} y`;
}

/* The Last touch filter, in the mockup's order. `maxDays` is inclusive;
   `minDays` is the "over a year" tail; `never` is the absence of a date. */
export const TOUCH_WINDOWS = [
  { key: "any", label: "Any" },
  { key: "7d", label: "7 days", maxDays: 7 },
  { key: "30d", label: "30 days", maxDays: 30 },
  { key: "90d", label: "90 days", maxDays: 90 },
  { key: "over_year", label: "Over a year", minDays: 366 },
  { key: "never", label: "Never", never: true },
];

/** The same shape for the Clients tab's Last job filter. */
export const JOB_WINDOWS = [
  { key: "any", label: "Any" },
  { key: "90d", label: "90 days", maxDays: 90 },
  { key: "year", label: "Year", maxDays: 365 },
  { key: "over_year", label: "Over a year", minDays: 366 },
  { key: "never", label: "Never", never: true },
];

export function matchesWindow(at, windowKey, windows, now = new Date()) {
  const w = (windows || []).find((x) => x.key === windowKey);
  if (!w || w.key === "any") return true;
  const days = daysSince(at, now);
  if (w.never) return days === null;
  if (days === null) return false;
  if (w.maxDays != null) return days <= w.maxDays;
  if (w.minDays != null) return days >= w.minDays;
  return true;
}

/** True when a date falls inside the trailing N months ending now. The Clients
 *  tab's "Revenue, 12 mo" column is this window, so it is one function and one
 *  test rather than an inline 365 in three places.
 *
 *  A date AFTER now is out of the window. It is the trailing twelve months, not
 *  the twelve months either side: a job booked for next March is money the
 *  business has not earned, and daysSince()'s display clamp would otherwise
 *  read it as "0 days ago" and add it to this year's revenue. */
export function withinMonths(date, months = 12, now = new Date()) {
  const days = signedDaysSince(date, now);
  if (days === null || days < 0) return false;
  return days <= Math.round(months * 30.44);
}

/* ═══ consent ══════════════════════════════════════════════════════════════ */

/**
 * The Consent cell: a dot and a label.
 *
 * `tone` names the dot — "opted_in" green, "relationship" blue, "no_bulk"
 * hollow grey, "unsubscribed" red. An unsubscribe outranks the state it
 * carries, because that is the fact the operator has to see before writing to
 * this person. `nda` is true when the contact's client is flagged
 * `noModelSessions`, and the cell says so after the label.
 *
 * A contact with no consent field at all reads as No bulk: that is
 * BLANK_CONTACT's default and the safe answer for a record the migration has
 * not reached.
 */
export function consentCell(contact, client) {
  const consent = contact?.consent || {};
  const unsubscribed = consent.unsubscribed === true;
  const state = CONSENT_STATES[consent.state] ? consent.state : "no_bulk";
  return {
    tone: unsubscribed ? "unsubscribed" : state,
    state,
    label: unsubscribed ? "Unsubscribed" : CONSENT_STATES[state],
    nda: client?.noModelSessions === true,
  };
}

/* ═══ value ════════════════════════════════════════════════════════════════ */

export const VALUE_BUCKET_LABELS = { direct: "direct", viaCompany: "via company", referred: "referred" };

/** A quote still in play — not booked, not completed, not lost, not a draft or
 *  a trashed record. Only an open quote can stand in for revenue on a row. */
export function isOpenQuote(q) {
  if (!q || q.draft || q.deletedAt) return false;
  const status = key(q.jobStatus);
  return status !== "booked" && status !== "completed" && status !== "lost";
}

/** What a quote is worth on a summary line. resolveQuoteRevenue prices the
 *  line items (the builder's own answer); `grandTotal` is what older records
 *  and the e2e fixture carry. */
export function quoteTotal(q) {
  const resolved = resolveQuoteRevenue(q);
  if (resolved > 0) return resolved;
  const cached = Number(q?.grandTotal ?? q?.total);
  return Number.isFinite(cached) && cached > 0 ? cached : 0;
}

/** The newest open quote in a list, by quote date then id. */
export function newestOpenQuote(quotes) {
  const open = (quotes || []).filter(isOpenQuote);
  if (open.length === 0) return null;
  return [...open].sort((a, b) =>
    (dateOnly(b.quoteDate) || "").localeCompare(dateOnly(a.quoteDate) || "") ||
    String(b.id || "").localeCompare(String(a.id || "")))[0];
}

/**
 * The Value cell for a contact.
 *
 * The primary number is the largest of the three buckets, because that is the
 * one that says what this person is to the business — a referrer's $41,200 of
 * referred work matters more than the $0 they personally signed. Ties fall to
 * direct, then via company, so a person with equal direct and referred money
 * reads as their own.
 *
 * All three at zero and an open quote on file reads "$0 quoted $23,850".
 * Nothing at all reads "—".
 */
export function valueCell(buckets, openQuote) {
  const b = buckets || {};
  const order = ["direct", "viaCompany", "referred"];
  let best = null;
  for (const name of order) {
    const amount = Number(b[name]?.amount) || 0;
    if (!best || amount > best.amount) best = { bucket: name, amount, count: Number(b[name]?.count) || 0 };
  }
  if (best && best.amount > 0) {
    return { amount: best.amount, bucket: best.bucket, note: VALUE_BUCKET_LABELS[best.bucket], quoted: 0 };
  }
  const quoted = openQuote ? quoteTotal(openQuote) : 0;
  if (quoted > 0) return { amount: 0, bucket: null, note: "quoted", quoted };
  return { amount: null, bucket: null, note: "", quoted: 0 };
}

/** Sorting key for the Value column: the same number the cell shows, with a
 *  quoted-only row ranked below every row that has real money. */
export const valueSortKey = (cell) => (cell?.amount ? cell.amount : 0);

/* ═══ open items ═══════════════════════════════════════════════════════════ */

/**
 * The oldest unresolved open item for one client.
 *
 * The caller hands in only `type: "open_item"` records with `status: "open"`
 * (that is the whole query — see useOpenItems.js), so this just picks. Oldest
 * first, because the one that has been sitting longest is the one costing
 * money; ties break on id so the column never flickers between two same-day
 * items.
 */
export function pickOpenItem(records, clientId) {
  const k = key(clientId);
  if (!k) return null;
  const mine = (records || []).filter((r) => r && key(r.clientId) === k && (r.status || "open") === "open");
  if (mine.length === 0) return null;
  return [...mine].sort((a, b) =>
    (dateOnly(a.date) || "9999").localeCompare(dateOnly(b.date) || "9999") ||
    String(a.id || "").localeCompare(String(b.id || "")))[0];
}

/** Every open item, grouped by client and already in pickOpenItem's order, so
 *  a list of 3,400 rows walks the collection once rather than once per row. */
export function buildOpenItemIndex(records = []) {
  const out = new Map();
  for (const r of records || []) {
    if (!r || (r.status || "open") !== "open") continue;
    const k = key(r.clientId);
    if (!k) continue;
    if (!out.has(k)) out.set(k, []);
    out.get(k).push(r);
  }
  for (const list of out.values()) {
    list.sort((a, b) =>
      (dateOnly(a.date) || "9999").localeCompare(dateOnly(b.date) || "9999") ||
      String(a.id || "").localeCompare(String(b.id || "")));
  }
  return out;
}

/** The column is one or two words, in red. The rest of the sentence is in the
 *  drawer's Activity timeline, which is where it can be read properly. */
export function openItemLabel(record) {
  const words = String(record?.summary || "").trim().split(/\s+/).filter(Boolean);
  return words.slice(0, 2).join(" ").replace(/[.,;:]$/, "");
}

/* ═══ the revenue bar ══════════════════════════════════════════════════════ */

/** Width of the 70px bar, relative to the largest 12-month figure in the
 *  filtered list. No max (an empty or all-zero list) is a bar of nothing, not
 *  a division by zero. */
export function barPct(value, max) {
  const v = Number(value) || 0;
  const m = Number(max) || 0;
  if (m <= 0 || v <= 0) return 0;
  return Math.min(100, Math.round((v / m) * 100));
}

/* ═══ contact rows ═════════════════════════════════════════════════════════ */

const contactClientIds = (contact) =>
  [contact?.clientId, ...asArray(contact?.linkedClients).map((l) => l?.clientId)].filter(Boolean);

/** Quotes reachable from a contact, by the two links the CRM actually has:
 *  the snapshotted contactName, and the clients they are linked to. */
export function buildQuoteLookup(quotes = [], clients = []) {
  const byContactName = new Map();
  const byClientId = new Map();
  const clientIdByName = new Map();
  for (const cl of clients || []) if (cl?.name) clientIdByName.set(key(cl.name), cl.id);
  const push = (map, k, q) => {
    if (!k) return;
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(q);
  };
  for (const q of quotes || []) {
    if (!q || q.deletedAt || q.draft) continue;
    push(byContactName, key(q.contactName), q);
    const clientId = q.clientId || clientIdByName.get(key(q.clientName));
    push(byClientId, key(clientId), q);
  }
  return { byContactName, byClientId };
}

/**
 * One row per contact, with every cell already derived.
 *
 * Everything expensive happens once here — the value index walks the jobs a
 * single time for all 3,400 people — so filtering and sorting downstream are
 * plain array work over precomputed fields.
 */
export function buildContactRows({ contacts = [], clients = [], jobs = [], quotes = [], now = new Date() } = {}) {
  const clientById = new Map();
  for (const cl of clients || []) if (cl?.id) clientById.set(key(cl.id), cl);
  const values = contactValueIndex({ contacts, jobs, clients, quotes });
  const lookup = buildQuoteLookup(quotes, clients);

  return (contacts || []).map((ct) => {
    const clientIds = contactClientIds(ct);
    const primaryClient = clientIds.map((id) => clientById.get(key(id))).find(Boolean) || null;
    const buckets = values.get(key(ct.id)) || { direct: {}, viaCompany: {}, referred: {} };

    const reachable = [
      ...(lookup.byContactName.get(key(ct.name)) || []),
      ...clientIds.flatMap((id) => lookup.byClientId.get(key(id)) || []),
    ];
    const seen = new Set();
    const value = valueCell(buckets, newestOpenQuote(reachable.filter((q) => {
      if (seen.has(q.id)) return false;
      seen.add(q.id);
      return true;
    })));

    const touch = lastTouchOf(ct);
    const roles = asArray(ct.role).length ? asArray(ct.role) : ["prospect"];
    const linkedin = ct.social?.linkedin || {};

    return {
      id: ct.id,
      record: ct,
      client: primaryClient,
      name: ct.name || "",
      company: ct.company || primaryClient?.name || "",
      email: asArray(ct.emails).find(Boolean) || "",
      roles,
      roleLabels: roles.map((r) => CONTACT_ROLES[r] || r),
      consent: consentCell(ct, primaryClient),
      buckets,
      value,
      touch,
      touchRel: relativeTime(touch.at, now),
      linkedin: {
        connected: linkedin.connected === true,
        url: typeof linkedin === "string" ? linkedin : linkedin.url || "",
      },
      industry: asArray(ct.industry),
      tags: asArray(ct.tags),
      sortName: key(ct.name),
      sortValue: valueSortKey(value),
      sortTouch: touch.at || "",
    };
  });
}

export const CONTACT_SORTS = [
  { key: "touch", label: "Last touch" },
  { key: "value", label: "Value" },
  { key: "name", label: "Name" },
];

export function sortContactRows(rows, sortKey) {
  const list = [...(rows || [])];
  if (sortKey === "value") {
    return list.sort((a, b) => b.sortValue - a.sortValue || a.sortName.localeCompare(b.sortName));
  }
  if (sortKey === "name") return list.sort((a, b) => a.sortName.localeCompare(b.sortName));
  // Last touch, newest first; a contact never touched sorts last, then by name.
  return list.sort((a, b) =>
    (b.sortTouch || "").localeCompare(a.sortTouch || "") || a.sortName.localeCompare(b.sortName));
}

/* Role chips, in the mockup's order. `All` matches everything; a contact with
   several roles matches any chip it carries. */
export const ROLE_CHIPS = [
  { key: "all", label: "All" },
  { key: "customer", label: "Customers" },
  { key: "prospect", label: "Prospects" },
  { key: "qualified", label: "Qualified" },
  { key: "referrer", label: "Referrers" },
  { key: "vendor", label: "Vendors" },
];

export function filterContactRows(rows, { role = "all", consent = "any", industry = "any", touch = "any" } = {}, now = new Date()) {
  return (rows || []).filter((r) => {
    if (role !== "all" && !r.roles.includes(role)) return false;
    if (consent !== "any" && r.consent.tone !== consent) return false;
    if (industry !== "any" && !r.industry.includes(industry)) return false;
    if (!matchesWindow(r.touch.at, touch, TOUCH_WINDOWS, now)) return false;
    return true;
  });
}

/* The Consent filter. "No bulk" deliberately does NOT include unsubscribed
   rows: an unsubscribe is its own answer and its own option, and folding it
   into no_bulk would hide the people who asked to be left alone inside the
   people we simply never asked. */
export const CONSENT_FILTERS = [
  { key: "any", label: "Any" },
  { key: "opted_in", label: "Opted in" },
  { key: "relationship", label: "Relationship" },
  { key: "no_bulk", label: "No bulk" },
  { key: "unsubscribed", label: "Unsubscribed" },
];

export const INDUSTRY_FILTERS = [
  { key: "any", label: "Any" },
  ...Object.entries(INDUSTRY_LABELS).map(([k, label]) => ({ key: k, label })),
];

/* ═══ client rows ══════════════════════════════════════════════════════════ */

/** Which client a job belongs to, by the three links jobs actually carry. */
export function buildClientJobIndex({ clients = [], jobs = [], quotes = [] } = {}) {
  const byId = new Map();
  const byCode = new Map();
  const byName = new Map();
  for (const cl of clients || []) {
    if (!cl?.id) continue;
    byId.set(key(cl.id), cl.id);
    if (cl.clientCode) byCode.set(key(cl.clientCode), cl.id);
    if (cl.name) byName.set(key(cl.name), cl.id);
  }
  const live = (jobs || []).filter((j) => j && !j.deletedAt);
  const out = new Map();
  for (const j of live) {
    // A change order belongs to the project it amends; its money is already
    // inside the parent's total, and counting the row would double the job.
    if (isRolledUpChangeOrderJob(j, live)) continue;
    const clientId = byId.get(key(j.clientId)) || byCode.get(key(j.clientCode)) || byName.get(key(j.clientName));
    if (!clientId) continue;
    const k = key(clientId);
    if (!out.has(k)) out.set(k, []);
    out.get(k).push({ job: j, amount: jobAmount(j, quotes, live), date: jobDate(j) });
  }
  for (const list of out.values()) list.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  return out;
}

/**
 * Newest hold date per client, in ONE pass over the holds.
 *
 * A hold is keyed by its quote id (holds slice 2), so the quote is the
 * reliable link and the hold's own `clientName` is only a fallback. Matching
 * on the stored name first was wrong in both directions: an account renamed
 * after the hold was written lost its held dates, and a hold whose quote
 * clearly belongs elsewhere could still be claimed by whoever now carries the
 * old name. So: if the hold's quote names a client id, that id decides, full
 * stop. Only a hold with no quote (or a quote with no clientId) falls back to
 * a name.
 *
 * Returns two maps rather than one so buildClientRows can look a client up by
 * id and by name without re-walking the holds per row.
 */
export function buildHoldIndex({ holds = [], quotes = [] } = {}) {
  const quoteById = new Map((quotes || []).map((q) => [key(q?.id), q]));
  const byClientId = new Map();
  const byClientName = new Map();
  const put = (map, k, date) => {
    if (!k || !date) return;
    if ((map.get(k) || "") < date) map.set(k, date);
  };
  for (const h of holds || []) {
    let newest = "";
    for (const d of asArray(h?.dates)) {
      const date = dateOnly(d?.date);
      if (date > newest) newest = date;
    }
    if (!newest) continue;
    const q = quoteById.get(key(h?.id));
    const quoteClientId = key(q?.clientId);
    if (quoteClientId) put(byClientId, quoteClientId, newest);
    else put(byClientName, key(q?.clientName) || key(h?.clientName), newest);
  }
  return { byClientId, byClientName };
}

/** The newest held date for one client, out of a prebuilt index. */
export function holdDateFor(index, client) {
  const byId = index?.byClientId?.get(key(client?.id)) || "";
  const byName = index?.byClientName?.get(key(client?.name)) || "";
  return byId > byName ? byId : byName;
}

/** The newest date a hold covers for a client, or "" when it has none. The
 *  one-client form of buildHoldIndex + holdDateFor; a list uses those two. */
export function lastHoldDate(client, holds = [], quotes = []) {
  return holdDateFor(buildHoldIndex({ holds, quotes }), client);
}

export function buildClientRows({ clients = [], contacts = [], jobs = [], quotes = [], holds = [], openItems = [], now = new Date() } = {}) {
  /* Everything that would otherwise be re-derived per row is built once here:
     the job index, the quote lookup, the client and contact maps, the holds
     and the open items. A 400-client book used to re-filter every hold and
     every open item inside the map below, which is quadratic in the two things
     most likely to grow. */
  const jobIndex = buildClientJobIndex({ clients, jobs, quotes });
  const lookup = buildQuoteLookup(quotes, clients);
  const clientById = new Map((clients || []).map((cl) => [key(cl?.id), cl]));
  const contactById = new Map((contacts || []).map((c) => [key(c?.id), c]));
  const holdIndex = buildHoldIndex({ holds, quotes });
  const openIndex = buildOpenItemIndex(openItems);

  return (clients || []).map((cl) => {
    const entries = jobIndex.get(key(cl.id)) || [];
    const lifetime = entries.reduce((s, e) => s + e.amount, 0);
    const last12 = entries.filter((e) => withinMonths(e.date, 12, now)).reduce((s, e) => s + e.amount, 0);
    const lastJob = entries.find((e) => e.date)?.date || "";
    const held = holdDateFor(holdIndex, cl);
    const parent = cl.parentClientId ? clientById.get(key(cl.parentClientId)) : null;
    const linked = asArray(cl.linkedContacts)
      .map((l) => ({ link: l, contact: contactById.get(key(l?.contactId)) }))
      .filter((x) => x.contact);
    const openItem = openIndex.get(key(cl.id))?.[0] || null;
    const quoted = last12 > 0 ? null : newestOpenQuote(lookup.byClientId.get(key(cl.id)) || []);
    // The newest thing on the calendar: a hold beyond the last job is what the
    // account is actually waiting on, and the column says so.
    const recent = held > lastJob ? { date: held, held: true } : { date: lastJob, held: false };

    return {
      id: cl.id,
      record: cl,
      name: cl.name || "",
      clientCode: cl.clientCode || "",
      subtitle: cl.orgDomain || parent?.name || "",
      parent,
      industry: asArray(cl.industry),
      status: cl.status || "active",
      contactCount: asArray(cl.linkedContacts).length,
      linkedContacts: linked,
      jobCount: entries.length,
      jobs: entries,
      lifetime,
      last12,
      quotedTotal: quoted ? quoteTotal(quoted) : 0,
      lastJob: recent.date,
      lastJobHeld: recent.held,
      openItem,
      openItemLabel: openItemLabel(openItem),
      nda: cl.noModelSessions === true,
      sortName: key(cl.name),
    };
  });
}

export const CLIENT_STATUS_CHIPS = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "inactive", label: "Inactive" },
  { key: "closed", label: "Closed" },
];

export const CLIENT_SORTS = [
  { key: "rev12", label: "Revenue, 12 months" },
  { key: "lifetime", label: "Lifetime" },
  { key: "name", label: "Name" },
  { key: "lastjob", label: "Last job" },
];

export function filterClientRows(rows, { status = "all", industry = "any", lastJob = "any" } = {}, now = new Date()) {
  return (rows || []).filter((r) => {
    if (status !== "all" && r.status !== status) return false;
    if (industry !== "any" && !r.industry.includes(industry)) return false;
    if (!matchesWindow(r.lastJob, lastJob, JOB_WINDOWS, now)) return false;
    return true;
  });
}

export function sortClientRows(rows, sortKey) {
  const list = [...(rows || [])];
  if (sortKey === "lifetime") return list.sort((a, b) => b.lifetime - a.lifetime || a.sortName.localeCompare(b.sortName));
  if (sortKey === "name") return list.sort((a, b) => a.sortName.localeCompare(b.sortName));
  if (sortKey === "lastjob") {
    return list.sort((a, b) => (b.lastJob || "").localeCompare(a.lastJob || "") || a.sortName.localeCompare(b.sortName));
  }
  return list.sort((a, b) => b.last12 - a.last12 || a.sortName.localeCompare(b.sortName));
}

/* ═══ drawer helpers ═══════════════════════════════════════════════════════ */

/**
 * The Tags field, from the raw string the operator is still typing.
 *
 * The drawers hold the tags input as text (`_tagsText`) and only split it
 * here, on save. Splitting on every keystroke made the field impossible to
 * type in: "alpha, beta" lost the space and then the comma the moment either
 * was typed, because a split/trim/filter round trip cannot represent a tag
 * that is halfway written.
 *
 * Order is the order they were typed; a repeat is dropped, comparing
 * case-insensitively but keeping the first spelling.
 */
export function parseTags(text) {
  const out = [];
  const seen = new Set();
  for (const part of String(text ?? "").split(",")) {
    const tag = part.trim();
    if (!tag) continue;
    const k = key(tag);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(tag);
  }
  return out;
}

/**
 * Apply the result of an async save only if the record it belongs to is still
 * the one on screen.
 *
 * A save is a network round trip. Click Save on Ben, then open Dave while it
 * is in flight, and the unguarded version put Ben back in the drawer when the
 * write landed — the operator loses their place and, worse, is now looking at
 * a record they did not choose. Returns whether it applied.
 */
export function commitIfStillActive(currentId, record, apply) {
  const id = record?.id;
  if (!id || String(id) !== String(currentId ?? "")) return false;
  apply(record);
  return true;
}

/* ═══ filters → segment rules ═════════════════════════════════════════════
   "Save as segment" on either filter row. The conversion is here, next to the
   filters themselves, so it cannot drift from what the row actually does — and
   it is pure, so what a chip means as a rule is unit-tested rather than
   eyeballed in a browser.

   The consent filter is deliberately NOT carried over. A segment's consent row
   is locked to the two bulk-eligible states (src/lib/segments.js), so a list
   narrowed to "Unsubscribed" cannot become an audience — and should not. */

/* The two consent filters a segment cannot be: `no_bulk` and `unsubscribed`
   are the people bulk mail may never go to, and a segment's consent row can
   only ever narrow WITHIN the bulk-eligible pair. A list filtered to either
   one has no audience in it, so Save as segment is disabled rather than
   silently dropping the filter and saving a wider audience than the screen. */
export const UNSAVEABLE_CONSENT = ["no_bulk", "unsubscribed"];

/** False when the Contacts filter row cannot honestly become an audience. */
export const canSaveContactsAsSegment = ({ consent = "any" } = {}) =>
  !UNSAVEABLE_CONSENT.includes(consent);

/** Why not, for the button's title. "" when it can. */
export const contactsSaveBlockedReason = ({ consent = "any" } = {}) => (
  canSaveContactsAsSegment({ consent })
    ? ""
    : `A segment can only ever go to people who opted in or have a relationship, so a list filtered to "${
      consent === "unsubscribed" ? "Unsubscribed" : "No bulk"}" cannot be saved as one`
);

/**
 * The Contacts filter row, as segment rules.
 *
 * The consent filter converts too, but only downward: `opted_in` and
 * `relationship` narrow the segment's locked consent row, and the two states
 * a segment may never carry are refused above rather than dropped here —
 * dropping one silently produced an audience wider than the list it came from.
 */
export function contactFiltersToRules({ role = "all", consent = "any", industry = "any", touch = "any" } = {}) {
  const rules = [];
  if (role !== "all") rules.push({ field: "role", op: "any_of", value: [role] });
  if (industry !== "any") rules.push({ field: "industry", op: "any_of", value: [industry] });
  const w = TOUCH_WINDOWS.find((x) => x.key === touch);
  if (w && w.never) rules.push({ field: "last_touch", op: "never" });
  else if (w && w.maxDays != null) rules.push({ field: "last_touch", op: "within", value: w.maxDays });
  else if (w && w.minDays != null) rules.push({ field: "last_touch", op: "over", value: w.minDays });
  if (!UNSAVEABLE_CONSENT.includes(consent) && consent !== "any") {
    rules.push({ field: "consent", op: "is", value: [consent] });
  }
  return rules;
}

/**
 * The Clients filter row, as segment rules.
 *
 * A segment is an audience of PEOPLE, and status, industry and last-job are
 * questions about accounts — so rather than trying to restate three client
 * filters as contact rules (which would ask a person's own industry, not their
 * employer's), the visible accounts themselves become one `client` rule. What
 * you are looking at is exactly what you get.
 */
export function clientFiltersToRules(rows = []) {
  const ids = (rows || []).map((r) => r?.id).filter(Boolean);
  return ids.length ? [{ field: "client", op: "any_of", value: ids }] : [];
}

/**
 * May this client list be saved as a segment at all?
 *
 * The Clients conversion is not like the Contacts one. Contacts turns FILTER
 * VALUES into rules, so an unfiltered list means "no constraint" and a segment
 * of everybody is the right answer. Clients turns the VISIBLE ROWS into one
 * `client any_of` rule, so an empty list means "nobody" — and returning no
 * rules for it would leave the segment carrying only its locked consent row,
 * which resolves to every bulk-eligible contact in the book. The widest
 * possible audience is the last thing a filter that matched nothing should
 * produce, so the button is disabled instead.
 *
 * `resolveSegment` also refuses an empty `client any_of` on its own (a rule
 * with nothing chosen matches nobody), and `validateSegment` refuses to save
 * one. This is the third fence, and the only one the operator ever sees.
 */
export const canSaveClientsAsSegment = (rows = []) => (rows || []).some((r) => r?.id);

/* ═══ paging ═══════════════════════════════════════════════════════════════ */

export const PAGE_SIZE = 40;

/** Only the visible page is ever rendered; sorting and filtering happen over
 *  the whole list above this. A page number past the end clamps to the last
 *  page, which is what a filter narrowing under the current page does. */
export function paginate(rows, page = 1, size = PAGE_SIZE) {
  const total = (rows || []).length;
  const pages = Math.max(1, Math.ceil(total / size));
  const current = Math.min(Math.max(1, page), pages);
  const from = total === 0 ? 0 : (current - 1) * size + 1;
  const to = Math.min(total, current * size);
  return { rows: (rows || []).slice((current - 1) * size, current * size), page: current, pages, from, to, total };
}

export function showingText({ from, to, total }, noun = "row") {
  if (!total) return `No ${noun}s`;
  return `Showing ${from.toLocaleString()} to ${to.toLocaleString()} of ${total.toLocaleString()}`;
}

/** The page numbers a footer shows: the ends, the neighbourhood of the current
 *  page, and an ellipsis for whatever is skipped. */
export function pageNumbers(page, pages) {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
  const out = new Set([1, 2, pages, page - 1, page, page + 1]);
  const list = [...out].filter((n) => n >= 1 && n <= pages).sort((a, b) => a - b);
  const withGaps = [];
  list.forEach((n, i) => {
    if (i > 0 && n - list[i - 1] > 1) withGaps.push("…");
    withGaps.push(n);
  });
  return withGaps;
}
